import React, { useState, useEffect } from 'react';
import '../styles/modal.css';

const BackgroundInfoModal = ({ isOpen, onClose, onSave, currentInfo }) => {
    const [formData, setFormData] = useState({
        allergies: '',
        dislikes: '',
        likes: '',
        generalInfo: '',
        privacyAcknowledged: false
    });
    const [error, setError] = useState('');

    // Handle body scroll when modal is open
    useEffect(() => {
        if (isOpen) {
            document.body.classList.add('modal-open');
        } else {
            document.body.classList.remove('modal-open');
        }

        // Cleanup on unmount
        return () => {
            document.body.classList.remove('modal-open');
        };
    }, [isOpen]);

    useEffect(() => {
        if (isOpen && currentInfo) {
            setFormData({
                allergies: currentInfo.allergies || '',
                dislikes: currentInfo.dislikes || '',
                likes: currentInfo.likes || '',
                generalInfo: currentInfo.generalInfo || '',
                privacyAcknowledged: currentInfo.privacyAcknowledged || false
            });
        }
    }, [isOpen, currentInfo]);

    const handleSubmit = (e) => {
        e.preventDefault();
        
        if (!formData.privacyAcknowledged) {
            setError('You must acknowledge the privacy notice to continue.');
            return;
        }

        setError('');
        onSave(formData);
        onClose();
    };

    const handleClose = () => {
        setFormData({
            allergies: '',
            dislikes: '',
            likes: '',
            generalInfo: '',
            privacyAcknowledged: false
        });
        setError('');
        onClose();
    };

    const handleOverlayClick = (e) => {
        // Only close if clicking the overlay, not the modal content
        if (e.target === e.currentTarget) {
            handleClose();
        }
    };

    // Don't render anything if modal is not open
    if (!isOpen) return null;

    return (
        <div className="modal-overlay" onClick={handleOverlayClick}>
            <div className="modal-content" onClick={(e) => e.stopPropagation()}>
                <div className="modal-header">
                    <h2>Personal Information</h2>
                    <button className="modal-close" onClick={handleClose} aria-label="Close modal">
                        ×
                    </button>
                </div>
                
                <form onSubmit={handleSubmit} className="modal-form">
                    <div className="form-group">
                        <label className="form-label" htmlFor="allergies">Allergies & Dietary Restrictions</label>
                        <textarea
                            id="allergies"
                            className="form-textarea"
                            value={formData.allergies}
                            onChange={(e) => setFormData(prev => ({ ...prev, allergies: e.target.value }))}
                            placeholder="e.g., Peanuts, Gluten, Dairy, Shellfish..."
                            rows="3"
                        />
                        <small>List any food allergies, intolerances, or dietary restrictions</small>
                    </div>

                    <div className="form-group">
                        <label className="form-label" htmlFor="dislikes">Food Dislikes</label>
                        <textarea
                            id="dislikes"
                            className="form-textarea"
                            value={formData.dislikes}
                            onChange={(e) => setFormData(prev => ({ ...prev, dislikes: e.target.value }))}
                            placeholder="e.g., Mushrooms, Cilantro, Spicy food..."
                            rows="3"
                        />
                        <small>Foods you prefer to avoid (not allergies)</small>
                    </div>

                    <div className="form-group">
                        <label className="form-label" htmlFor="likes">Food Preferences</label>
                        <textarea
                            id="likes"
                            className="form-textarea"
                            value={formData.likes}
                            onChange={(e) => setFormData(prev => ({ ...prev, likes: e.target.value }))}
                            placeholder="e.g., Italian cuisine, Vegetarian options, Quick meals..."
                            rows="3"
                        />
                        <small>Foods and cuisines you enjoy</small>
                    </div>

                    <div className="form-group">
                        <label className="form-label" htmlFor="generalInfo">Additional Information</label>
                        <textarea
                            id="generalInfo"
                            className="form-textarea"
                            value={formData.generalInfo}
                            onChange={(e) => setFormData(prev => ({ ...prev, generalInfo: e.target.value }))}
                            placeholder="Any other information that might be relevant for recipe recommendations..."
                            rows="4"
                        />
                        <small>Optional: Cooking experience, household size, time constraints, etc.</small>
                    </div>

                    <div className="form-group privacy-notice">
                        <div className="privacy-warning">
                            <h4>⚠️ Privacy Notice</h4>
                            <p>
                                <strong>This is NOT Fort Knox.</strong> While we do not willingly share your personal information, 
                                please be aware that this website is not secured to the level of hospitals or medical facilities.
                            </p>
                            <p>
                                If you choose to store health-related information here (such as allergies or dietary restrictions), 
                                you acknowledge that there is an increased risk compared to storing such information in a 
                                HIPAA-compliant medical system.
                            </p>
                            <p>
                                <strong>Recommendation:</strong> For sensitive health information, consider using a dedicated 
                                medical app or consulting with your healthcare provider.
                            </p>
                        </div>
                        
                        <label className="checkbox-label">
                            <input
                                type="checkbox"
                                checked={formData.privacyAcknowledged}
                                onChange={(e) => setFormData(prev => ({ 
                                    ...prev, 
                                    privacyAcknowledged: e.target.checked 
                                }))}
                                required
                            />
                            <span className="checkmark"></span>
                            I acknowledge that I understand the privacy risks and choose to proceed
                        </label>
                    </div>

                    {error && <div className="error-message">{error}</div>}

                    <div className="modal-actions">
                        <button type="button" className="btn-secondary" onClick={handleClose}>
                            Cancel
                        </button>
                        <button type="submit" className="btn-primary">
                            Save Information
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default BackgroundInfoModal;